import { useState } from 'react';
import { Fingerprint, KeyRound, LayoutGrid, LogOut, MoreVertical } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@proerp/ui';
import { logout } from '@/lib/auth';
import { initials } from '@/lib/format';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { ModuleVisibilityDialog } from './ModuleVisibilityDialog';
import { SecuritySettingsDialog } from './SecuritySettingsDialog';

/**
 * Benutzermenü im Sidebar-Footer (Muster: shadcn dashboard-01 NavUser).
 * Dev-Sessions (Backend ohne Auth) zeigen nur eine statische Zeile.
 */
export function NavUser() {
  const { isMobile } = useSidebar();
  const { data } = useCurrentUser();
  const me = data?.data;
  const display = me?.name || me?.email || 'Benutzer';
  const isRealSession = Boolean(me && !me.isDevSession);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);
  const [isModulesOpen, setIsModulesOpen] = useState(false);

  // Im Icon-Modus (eingeklappte Sidebar) schrumpft das Avatar auf size-6 und
  // Text/Chevron verschwinden — sonst wird das Kürzel im 32-px-Button abgeschnitten.
  // Im (portalten) Dropdown greifen die group-Klassen nicht — dort bleibt alles sichtbar.
  const identity = (
    <>
      <Avatar className="size-8 rounded-lg group-data-[collapsible=icon]:size-6">
        <AvatarFallback className="rounded-lg bg-accent text-xs font-medium text-foreground">
          {initials(display)}
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
        <span className="truncate font-medium">{display}</span>
        <span className="truncate text-xs text-muted-foreground">
          {me?.isDevSession ? 'Dev-Session' : (me?.email ?? '')}
        </span>
      </div>
    </>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {isRealSession ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="group-data-[collapsible=icon]:justify-center data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                {identity}
                <MoreVertical
                  className="ml-auto size-4 group-data-[collapsible=icon]:hidden"
                  aria-hidden
                />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">{identity}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onSelect={() => setTimeout(() => setIsPasswordOpen(true), 0)}>
                  <KeyRound strokeWidth={1.5} aria-hidden />
                  Passwort ändern
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setTimeout(() => setIsSecurityOpen(true), 0)}>
                  <Fingerprint strokeWidth={1.5} aria-hidden />
                  Sicherheit
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setTimeout(() => setIsModulesOpen(true), 0)}>
                  <LayoutGrid strokeWidth={1.5} aria-hidden />
                  Module verwalten
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => logout()}>
                  <LogOut strokeWidth={1.5} aria-hidden />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <SidebarMenuButton
            size="lg"
            className="pointer-events-none group-data-[collapsible=icon]:justify-center"
          >
            {identity}
          </SidebarMenuButton>
        )}
      </SidebarMenuItem>
      <ChangePasswordDialog open={isPasswordOpen} onOpenChange={setIsPasswordOpen} />
      <SecuritySettingsDialog open={isSecurityOpen} onOpenChange={setIsSecurityOpen} />
      <ModuleVisibilityDialog open={isModulesOpen} onOpenChange={setIsModulesOpen} />
    </SidebarMenu>
  );
}
